/**
 * Control Account tab for v2.
 *
 * Every section is `opening + P&L + cash = closing`.
 *
 * Which rows are editable differs by section, and the difference is the point:
 *
 * - Trade debtors and creditors are DERIVED — invoicing comes from the P&L and
 *   COGS, the closing balance from the days ratio (or a known actual), and the
 *   cash line is the balancing figure. Editing them here would be overwritten
 *   on the next recalculation.
 * - The five sections v2 adds (deferred tax, other debtors, other creditors,
 *   other PAYE & NI, creditors over one year) carry hardcoded actuals in the
 *   template and are ordinary inputs here, so their P&L and cash rows are
 *   editable.
 *
 * Opening balances are scalars on the input model, not per-period rows, so the
 * opening row shown is the computed one — which for period 1 IS that scalar
 * and thereafter is the previous closing.
 */

import { useCallback } from 'react';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { ControlSectionComputed } from '../../../types/financial-model-v2';
import { V2Sheet } from './V2Table';

/**
 * Rows follow the template's order: Opening, the P&L line, any extra lines
 * (Add Vat; VAT Recovered), then the cash line — which is the balancing figure
 * of everything above it — and Closing.
 */
function DerivedSection({
  label,
  section,
  pnlLabel,
  cashLabel,
  extra = [],
  onClosingChange,
}: {
  label: string;
  section: ControlSectionComputed;
  pnlLabel: string;
  cashLabel: string;
  extra?: Array<{ label: string; data: Record<string, number> | undefined }>;
  /** An entered closing balance pins the section; the cash line then reconciles to it. */
  onClosingChange: (period: string, value: number) => void;
}) {
  return (
    <>
      <SectionHeader label={`${label} — derived`} />
      <ComputedDataRow label="Opening Balance" data={section.opening} />
      <ComputedDataRow label={pnlLabel} data={section.pnl} />
      {extra.map((row) => (
        <ComputedDataRow key={row.label} label={row.label} data={row.data ?? {}} />
      ))}
      <ComputedDataRow label={cashLabel} data={section.cash} />
      <V2EditableRow label="Closing Balance" data={section.closing} onCellChange={onClosingChange} />
    </>
  );
}

export function ControlAccountViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;
  const ca = computed.controlAccount;

  const editableSection = (
    label: string,
    key: 'deferredTaxAsset' | 'otherDebtors' | 'otherCreditors' | 'otherPayeNi' | 'creditorsOverOneYear',
  ) => (
    <>
      <SectionHeader label={label} />
      <ComputedDataRow label="Opening Balance" data={ca[key].opening} />
      <V2EditableRow
        label="P&L"
        data={inputs.controlAccount[key].pnl}
        onCellChange={handleChange(`controlAccount.${key}.pnl`)}
      />
      <V2EditableRow
        label="Cash"
        data={inputs.controlAccount[key].cash}
        onCellChange={handleChange(`controlAccount.${key}.cash`)}
      />
      {key === 'otherPayeNi' ? (
        <SubtotalRow label="Closing Balance" data={ca[key].closing} />
      ) : (
        <V2EditableRow
          label="Closing Balance"
          data={ca[key].closing}
          onCellChange={handleChange(`controlAccount.${key}ClosingActual`)}
        />
      )}
    </>
  );

  return (
    <V2Sheet>
      <DerivedSection
        label="Trade Debtors"
        onClosingChange={handleChange('controlAccount.tradeDebtorsClosingActual')}
        section={ca.tradeDebtors}
        pnlLabel="Invoiced (total revenue)"
        extra={[{ label: 'Add Vat', data: ca.tradeDebtorsVat }]}
        cashLabel="Receipts"
      />

      <DerivedSection
        label="Trade Creditors"
        onClosingChange={handleChange('controlAccount.tradeCreditorsClosingActual')}
        section={ca.tradeCreditors}
        pnlLabel="Invoiced (goods purchased)"
        extra={[{ label: 'Add Vat', data: ca.tradeCreditorsVat }]}
        cashLabel="Payments"
      />

      <DerivedSection
        label="VAT"
        onClosingChange={handleChange('controlAccount.vatClosingActual')}
        section={ca.vat}
        pnlLabel="VAT Charged"
        extra={[{ label: 'VAT Recovered', data: ca.vatRecovered }]}
        cashLabel="Payments"
      />

      <DerivedSection
        label="Corporation Tax"
        onClosingChange={handleChange('controlAccount.corporationTaxClosingActual')}
        section={ca.corporationTax}
        pnlLabel="Charge"
        cashLabel="Paid"
      />

      <SectionHeader label="Equity" />
      <V2EditableRow
        label="Investment"
        data={inputs.controlAccount.equityInvestment}
        onCellChange={handleChange('controlAccount.equityInvestment')}
      />
      <SubtotalRow label="Closing Balance" data={ca.equity.closing} />

      {editableSection('Deferred Tax Asset', 'deferredTaxAsset')}
      {editableSection('Other Debtors', 'otherDebtors')}
      {editableSection('Other Creditors', 'otherCreditors')}
      {editableSection('Other PAYE & NI', 'otherPayeNi')}
      {editableSection('Creditors over one Year', 'creditorsOverOneYear')}
    </V2Sheet>
  );
}
